import type { ParlayScore } from '@hooks/useStrategyStats';
import {
  type EdgeLegChange,
  formatChance,
  PERSONAL_SCORE_LABEL,
} from '@parlay-picker/shared/edgeStatus';
import { LEAGUE_HELD_NO_SIGNAL_DESCRIPTION } from '@parlay-picker/shared/mlRecommendation';
import {
  DISPLAY_THRESHOLDS,
  getNormalizedFromParlayScore,
  legGroupLabel,
  normalizeFactorsForDisplay,
} from '@parlay-picker/shared/scoreDisplay';
import { ArrowRight, Lock } from 'lucide-react';
import type React from 'react';
import { FactorChip, LegFactorChip } from './FactorChip';
import { PlayerBadge } from './PlayerBadge';
import { ScoreBar } from './ScoreBar';
import { getBadgeStyle, getScoreTextClass } from './scorePanelUtils';
import type { LegGroup } from './types';

interface ScorePanelTooltipProps {
  activeScore: ParlayScore;
  activeTab: 'bets' | 'ai';
  /** The approved explanation of a proven edge, shown with the personal score. */
  edgeHint?: string;
  /** Each leg's AI chance and its chance with the edge; the personal tab shows these, not factors. */
  edgeLegs?: EdgeLegChange[] | null;
  tooltipPosition: 'above' | 'below';
  scoringLocked: boolean;
  deepAnalysisLocked: boolean;
  onOpenUpgrade: (feature: 'scoring_breakdown' | 'scoring_deep_analysis') => void;
}

export function ScorePanelTooltip({
  activeScore,
  activeTab,
  edgeHint,
  edgeLegs,
  tooltipPosition,
  scoringLocked,
  deepAnalysisLocked,
  onOpenUpgrade,
}: ScorePanelTooltipProps): React.ReactElement {
  const { displayScore: activeDisplayScore, displayColor: activeDisplayColor } =
    getNormalizedFromParlayScore(activeScore);
  const style = getBadgeStyle(activeDisplayColor, activeScore.playerWarnings.length > 0);
  const hasWarnings = activeScore.playerWarnings.length > 0;
  const hasHighlights = activeScore.playerHighlights.length > 0;

  const thresholds = activeScore.thresholds
    ? { bet: activeScore.thresholds.bet, skip: activeScore.thresholds.skip }
    : undefined;
  const normalized = normalizeFactorsForDisplay(activeScore.score, activeScore.factors, thresholds);

  // Group normalized factors by leg
  const legGroups: LegGroup[] = [];
  const generalFactors = normalized.factors.filter((factor) => {
    if (factor.legIndex !== undefined && factor.playerName) {
      const key = `${factor.legIndex}-${factor.playerName}-${factor.propType || ''}`;
      let group = legGroups.find((g) => g.key === key);
      if (!group) {
        group = {
          key,
          label: legGroupLabel(factor.playerName, factor.propType),
          factors: [],
          netScore: 0,
        };
        legGroups.push(group);
      }
      group.factors.push(factor);
      group.netScore += factor.displayScore;
      return false;
    }
    return true;
  });

  legGroups.sort((a, b) => {
    const aIndex = a.factors[0]?.legIndex ?? 0;
    const bIndex = b.factors[0]?.legIndex ?? 0;
    return aIndex - bIndex;
  });

  const scoreColorClass = getScoreTextClass(activeDisplayColor);

  return (
    <div
      className={`absolute z-50 left-0 w-80 flex flex-col ${
        tooltipPosition === 'above' ? 'bottom-full flex-col-reverse' : 'top-full'
      }`}
    >
      {/* Invisible bridge to maintain hover continuity */}
      <div className="h-2" />
      {/* Tooltip content */}
      <div className="relative bg-theme-bg rounded-xl shadow-2xl border border-theme-border text-left max-h-112.5 overflow-y-auto">
        <div className="p-4">
          {/* Header with score */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white text-sm">
                {activeTab === 'bets' ? PERSONAL_SCORE_LABEL : '📊 AI Picks'}
              </span>
              {activeTab === 'ai' && (
                <span className="text-[10px] text-theme-text-tertiary">
                  From all AI predictions
                </span>
              )}
            </div>
            <span className={`text-xl font-bold ${scoreColorClass}`}>{activeDisplayScore}</span>
          </div>
          {activeTab === 'bets' && edgeHint && (
            <p className="mb-3 text-[11px] leading-relaxed text-theme-text-secondary">{edgeHint}</p>
          )}

          {/* Score bar */}
          <ScoreBar displayScore={activeDisplayScore} displayColor={activeDisplayColor} />

          {activeTab === 'bets' ? (
            edgeLegs && <EdgeLegRows rows={edgeLegs} />
          ) : scoringLocked ? (
            <LockedTeaserPanel
              activeScore={activeScore}
              legGroups={legGroups}
              onOpenUpgrade={onOpenUpgrade}
            />
          ) : deepAnalysisLocked ? (
            <ProTierPanel
              activeScore={activeScore}
              normalized={normalized}
              hasWarnings={hasWarnings}
              legGroups={legGroups}
              style={style}
              onOpenUpgrade={onOpenUpgrade}
            />
          ) : (
            <FullAnalysisPanel
              activeScore={activeScore}
              normalized={normalized}
              hasWarnings={hasWarnings}
              hasHighlights={hasHighlights}
              legGroups={legGroups}
              generalFactors={generalFactors}
              style={style}
              activeDisplayColor={activeDisplayColor}
              activeTab={activeTab}
            />
          )}
        </div>
        {/* Tooltip arrow */}
        <div
          className={`absolute left-6 w-3 h-3 bg-theme-bg transform rotate-45 ${
            tooltipPosition === 'above'
              ? '-bottom-1.5 border-r border-b border-theme-border'
              : '-top-1.5 border-l border-t border-theme-border'
          }`}
        />
      </div>
    </div>
  );
}

// ─── Tier sub-panels ────────────────────────────────────────────────────────

function EdgeLegRows({ rows }: { rows: EdgeLegChange[] }): React.ReactElement {
  return (
    <div
      className="mt-3 pt-3 border-t border-theme-border space-y-1"
      data-testid="edge-leg-changes"
    >
      {rows.map((row, i) => (
        <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
          <span className="min-w-0 truncate text-theme-text-secondary">{row.label}</span>
          <span className="shrink-0 font-mono text-theme-text">
            <span className="text-theme-text-tertiary">AI {formatChance(row.ai)}</span>
            <ArrowRight className="mx-1 inline h-3 w-3" aria-hidden />
            {formatChance(row.withEdge)}
          </span>
        </div>
      ))}
    </div>
  );
}

function LockedTeaserPanel({
  activeScore,
  legGroups,
  onOpenUpgrade,
}: {
  activeScore: ParlayScore;
  legGroups: LegGroup[];
  onOpenUpgrade: (feature: 'scoring_breakdown' | 'scoring_deep_analysis') => void;
}): React.ReactElement {
  return (
    <>
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[10px] text-theme-text-tertiary">
          {activeScore.factors.length} factors analyzed
        </span>
      </div>

      <div className="mt-3 pt-3 border-t border-theme-border space-y-2">
        <LockedSectionButton
          label="Factor Breakdown"
          count={String(activeScore.factors.length)}
          feature="scoring_breakdown"
          onOpenUpgrade={onOpenUpgrade}
        />
        {activeScore.playerWarnings.length > 0 && (
          <LockedSectionButton
            label="Player Warnings"
            labelClass="text-theme-warning/70"
            count={String(activeScore.playerWarnings.length)}
            feature="scoring_deep_analysis"
            onOpenUpgrade={onOpenUpgrade}
          />
        )}
        {activeScore.conflicts && activeScore.conflicts.length > 0 && (
          <LockedSectionButton
            label="Conflicts"
            labelClass="text-theme-warning/70"
            count={String(activeScore.conflicts.length)}
            feature="scoring_deep_analysis"
            onOpenUpgrade={onOpenUpgrade}
          />
        )}
        {legGroups.length > 0 && (
          <LockedSectionButton
            label="Per-Leg Analysis"
            count={`${legGroups.length} legs`}
            feature="scoring_deep_analysis"
            onOpenUpgrade={onOpenUpgrade}
          />
        )}
      </div>

      <button
        type="button"
        onClick={() => onOpenUpgrade('scoring_breakdown')}
        className="w-full mt-3 py-2 rounded-lg bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill text-xs font-medium transition"
      >
        Unlock with Pro
      </button>
    </>
  );
}

function ProTierPanel({
  activeScore,
  normalized,
  hasWarnings,
  legGroups,
  style,
  onOpenUpgrade,
}: {
  activeScore: ParlayScore;
  normalized: ReturnType<typeof normalizeFactorsForDisplay>;
  hasWarnings: boolean;
  legGroups: LegGroup[];
  style: ReturnType<typeof getBadgeStyle>;
  onOpenUpgrade: (feature: 'scoring_breakdown' | 'scoring_deep_analysis') => void;
}): React.ReactElement {
  return (
    <>
      <RecommendationRow activeScore={activeScore} style={style} />

      {normalized.factors.length > 0 && (
        <div className="mt-3 pt-3 border-t border-theme-border">
          <div className="text-[10px] text-theme-text-tertiary uppercase tracking-wider mb-2">
            Top Factors
          </div>
          <div className="space-y-1">
            {[...normalized.factors]
              .sort((a, b) => Math.abs(b.displayScore) - Math.abs(a.displayScore))
              .slice(0, 3)
              .map((factor, i) => (
                <FactorChip key={i} factor={factor} />
              ))}
          </div>
          {activeScore.factors.length > 3 && (
            <button
              type="button"
              onClick={() => onOpenUpgrade('scoring_deep_analysis')}
              className="w-full mt-1.5 text-[10px] text-theme-primary hover:text-theme-primary-hover transition-colors"
            >
              +{activeScore.factors.length - 3} more factors
            </button>
          )}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-theme-border space-y-2">
        {hasWarnings && (
          <LockedSectionButton
            label="Player Warnings"
            labelClass="text-theme-warning/70"
            count={String(activeScore.playerWarnings.length)}
            feature="scoring_deep_analysis"
            onOpenUpgrade={onOpenUpgrade}
          />
        )}
        {activeScore.conflicts && activeScore.conflicts.length > 0 && (
          <LockedSectionButton
            label="Conflicts"
            labelClass="text-theme-warning/70"
            count={String(activeScore.conflicts.length)}
            feature="scoring_deep_analysis"
            onOpenUpgrade={onOpenUpgrade}
          />
        )}
        {legGroups.length > 0 && (
          <LockedSectionButton
            label="Per-Leg Analysis"
            count={`${legGroups.length} legs`}
            feature="scoring_deep_analysis"
            onOpenUpgrade={onOpenUpgrade}
          />
        )}
      </div>

      <button
        type="button"
        onClick={() => onOpenUpgrade('scoring_deep_analysis')}
        className="w-full mt-3 py-2 rounded-lg bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill text-xs font-medium transition"
      >
        Unlock Full Analysis with Premium
      </button>
    </>
  );
}

function FullAnalysisPanel({
  activeScore,
  normalized,
  hasWarnings,
  hasHighlights,
  legGroups,
  generalFactors,
  style,
  activeDisplayColor,
  activeTab,
}: {
  activeScore: ParlayScore;
  normalized: ReturnType<typeof normalizeFactorsForDisplay>;
  hasWarnings: boolean;
  hasHighlights: boolean;
  legGroups: LegGroup[];
  generalFactors: ReturnType<typeof normalizeFactorsForDisplay>['factors'];
  style: ReturnType<typeof getBadgeStyle>;
  activeDisplayColor: ReturnType<typeof getNormalizedFromParlayScore>['displayColor'];
  activeTab: 'bets' | 'ai';
}): React.ReactElement {
  const totalColorClass = getScoreTextClass(activeDisplayColor);

  return (
    <>
      <RecommendationRow activeScore={activeScore} style={style} />

      {hasWarnings && (
        <div className="mt-3 pt-3 border-t border-theme-border">
          <div className="text-[10px] text-theme-warning uppercase tracking-wider mb-2 flex items-center gap-1">
            ⚠️ Players to Avoid
          </div>
          <div className="space-y-1.5">
            {activeScore.playerWarnings.map((pw, i) => (
              <PlayerBadge key={i} player={pw} type="warning" />
            ))}
          </div>
        </div>
      )}

      {hasHighlights && (
        <div className={`mt-3 ${hasWarnings ? '' : 'pt-3 border-t border-theme-border'}`}>
          <div className="text-[10px] text-theme-success uppercase tracking-wider mb-2 flex items-center gap-1">
            ⭐ Hot Players
          </div>
          <div className="space-y-1.5">
            {activeScore.playerHighlights.map((ph, i) => (
              <PlayerBadge key={i} player={ph} type="highlight" />
            ))}
          </div>
        </div>
      )}

      {activeScore.conflicts && activeScore.conflicts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-theme-border">
          <div className="text-[10px] text-theme-warning uppercase tracking-wider mb-2 flex items-center gap-1">
            ⚡ Mixed Signals
          </div>
          <div className="space-y-1.5">
            {activeScore.conflicts.slice(0, 3).map((conflict, i) => (
              <div
                key={i}
                className={`px-2.5 py-1.5 rounded-lg border ${
                  conflict.severity === 'high'
                    ? 'bg-theme-danger/15 border-theme-danger/30'
                    : conflict.severity === 'medium'
                      ? 'bg-orange-500/15 border-orange-400/30'
                      : 'bg-theme-warning/15 border-theme-warning/30'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-xs">
                    {conflict.severity === 'high'
                      ? '🔴'
                      : conflict.severity === 'medium'
                        ? '🟠'
                        : '🟡'}
                  </span>
                  <span className="text-xs text-theme-text flex-1">{conflict.description}</span>
                </div>
              </div>
            ))}
            {activeScore.conflicts.length > 3 && (
              <div className="text-[10px] text-theme-text-tertiary text-center">
                +{activeScore.conflicts.length - 3} more
              </div>
            )}
          </div>
        </div>
      )}

      {legGroups.length > 0 && (
        <div className="mt-3 pt-3 border-t border-theme-border">
          <div className="text-[10px] text-theme-text-tertiary uppercase tracking-wider mb-2">
            By Leg
          </div>
          <div className="space-y-3">
            {legGroups.map((group) => (
              <div key={group.key} className="bg-theme-surface/50 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-theme-text">{group.label}</span>
                  <span
                    className={`text-xs font-bold ${
                      group.netScore > 0
                        ? 'text-theme-success'
                        : group.netScore < 0
                          ? 'text-theme-danger'
                          : 'text-theme-text-secondary'
                    }`}
                  >
                    {group.netScore > 0 ? '+' : ''}
                    {group.netScore}
                  </span>
                </div>
                <div className="space-y-1">
                  {group.factors.map((factor, i) => (
                    <LegFactorChip key={i} factor={factor} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {generalFactors.length > 0 && (
        <div className="mt-3 pt-3 border-t border-theme-border">
          <div className="text-[10px] text-theme-text-tertiary uppercase tracking-wider mb-2">
            Parlay Analysis
          </div>
          <div className="space-y-1">
            {generalFactors.map((factor, i) => (
              <FactorChip key={i} factor={factor} />
            ))}
          </div>
        </div>
      )}

      {/* Score summation footer */}
      <div className="mt-3 pt-3 border-t border-theme-border">
        <div className="space-y-1">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[11px] text-theme-text-tertiary">Base</span>
            <span className="text-[11px] font-mono font-bold text-theme-text-secondary">
              {normalized.baseline}
            </span>
          </div>
          {normalized.baseAnalysis != null && (
            <div className="flex items-center justify-between px-2 py-1 rounded border bg-theme-surface/50 border-theme-border/50">
              <span className="text-[11px] text-theme-text-tertiary">Other factors</span>
              <span
                className={`text-[11px] font-mono font-bold ${
                  normalized.baseAnalysis > 0 ? 'text-theme-success' : 'text-theme-danger'
                }`}
              >
                {normalized.baseAnalysis > 0 ? '+' : ''}
                {normalized.baseAnalysis}
              </span>
            </div>
          )}
          <div className="border-t border-theme-border/50 mx-2" />
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[11px] text-theme-text-secondary font-medium">Total</span>
            <span className={`text-xs font-mono font-bold ${totalColorClass}`}>
              = {normalized.displayTotal}
            </span>
          </div>
        </div>
      </div>

      {/* Footer explanation */}
      <div className="mt-3 pt-3 border-t border-theme-border text-[10px] text-theme-text-tertiary">
        <p>
          <span className="text-theme-success">BET {DISPLAY_THRESHOLDS.BET}+</span>
          {' | '}
          <span className="text-teal-400">LEAN {DISPLAY_THRESHOLDS.LEAN_BET}+</span>
          {' | '}
          <span className="text-theme-warning">CAUTION {DISPLAY_THRESHOLDS.CAUTION}+</span>
          {' | '}
          <span className="text-theme-danger">SKIP &lt;{DISPLAY_THRESHOLDS.CAUTION}</span>
        </p>
        {activeTab === 'ai' && <p className="mt-1">Based on AI predictions</p>}
      </div>
    </>
  );
}

// ─── Shared locked section button ────────────────────────────────────────────

function RecommendationRow({
  activeScore,
  style,
}: {
  activeScore: ParlayScore;
  style: ReturnType<typeof getBadgeStyle>;
}): React.ReactElement {
  const { displayRec } = getNormalizedFromParlayScore(activeScore);
  return (
    <>
      <div className="mt-3 flex items-center gap-2">
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}
          data-testid="parlay-score-label"
        >
          {displayRec}
        </span>
        <span className="text-[10px] text-theme-text-tertiary">
          {activeScore.factors.length} factors analyzed
        </span>
      </div>
      {displayRec === 'NO SIGNAL' && (
        <p className="mt-1.5 text-[10px] leading-snug text-theme-text-tertiary">
          {LEAGUE_HELD_NO_SIGNAL_DESCRIPTION}
        </p>
      )}
    </>
  );
}

function LockedSectionButton({
  label,
  labelClass = 'text-theme-text-secondary',
  count,
  feature,
  onOpenUpgrade,
}: {
  label: string;
  labelClass?: string;
  count: string;
  feature: 'scoring_breakdown' | 'scoring_deep_analysis';
  onOpenUpgrade: (feature: 'scoring_breakdown' | 'scoring_deep_analysis') => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={() => onOpenUpgrade(feature)}
      className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg bg-theme-surface/60 border border-theme-border/50 hover:bg-theme-surface-hover transition-colors"
    >
      <div className="flex items-center gap-2">
        <Lock className="w-3.5 h-3.5 text-theme-text-tertiary" />
        <span className={`text-xs ${labelClass}`}>{label}</span>
      </div>
      <span className="text-[10px] text-theme-text-tertiary">{count}</span>
    </button>
  );
}
